
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Clock, ArrowLeft, User, Timer } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useServerTime } from '@/hooks/useServerTime';

interface Employee {
  id: string;
  employee_id: string;
  first_name: string;
  last_name: string;
}

interface TimeEntry {
  id: string;
  employee_id: string;
  clock_in_time: string;
  clock_out_time: string | null;
  total_hours: number | null;
}

interface StoreStationTimingProps {
  onBack: () => void;
}

export const StoreStationTiming = ({ onBack }: StoreStationTimingProps) => {
  const [employeeId, setEmployeeId] = useState("");
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [activeEntry, setActiveEntry] = useState<TimeEntry | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { serverTime, calculateTimeWorked, formatDuration, isTimeSynced } = useServerTime();

  const lookupEmployee = async () => {
    if (!employeeId.trim()) {
      toast({
        title: "Employee ID Required",
        description: "Please enter your employee ID.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data: employeeData, error: employeeError } = await supabase
        .rpc('get_employee_by_id', { 
          emp_id: employeeId.trim() 
        });

      if (employeeError || !employeeData || employeeData.length === 0) {
        toast({
          title: "Employee Not Found",
          description: "Invalid employee ID or account is inactive.",
          variant: "destructive"
        });
        return;
      }

      const emp = employeeData[0] as Employee;
      setEmployee(emp);

      const { data: activeEntryData } = await supabase
        .rpc('get_active_time_entry', { 
          emp_id: emp.id 
        });

      if (activeEntryData && activeEntryData.length > 0) {
        setActiveEntry(activeEntryData[0] as TimeEntry);
      } else {
        setActiveEntry(null);
      }

    } catch (error) {
      console.error('Employee lookup error:', error);
      toast({
        title: "Error",
        description: "Failed to lookup employee information.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clockIn = async () => {
    if (!employee) return;

    setIsLoading(true);
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const ipData = await response.json();

      const { data, error } = await supabase
        .rpc('clock_in_employee', {
          emp_id: employee.id,
          clock_in_location_data: { store_station: true },
          clock_in_ip_address: ipData.ip
        });

      if (error) throw error;

      setActiveEntry(data as TimeEntry);
      toast({
        title: "Clocked In Successfully",
        description: `Welcome ${employee.first_name}! You are now clocked in.`,
      });

    } catch (error) {
      console.error('Clock in error:', error);
      toast({
        title: "Clock In Failed",
        description: "Failed to clock in. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clockOut = async () => {
    if (!employee || !activeEntry) return;

    setIsLoading(true);
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const ipData = await response.json();

      const { error } = await supabase
        .rpc('clock_out_employee', {
          time_entry_id: activeEntry.id,
          clock_out_location_data: { store_station: true },
          clock_out_ip_address: ipData.ip
        });

      if (error) throw error;

      setActiveEntry(null);
      toast({
        title: "Clocked Out Successfully",
        description: `Goodbye ${employee.first_name}! You have been clocked out.`,
      });

    } catch (error) {
      console.error('Clock out error:', error);
      toast({
        title: "Clock Out Failed",
        description: "Failed to clock out. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const reset = () => {
    setEmployee(null);
    setActiveEntry(null);
    setEmployeeId("");
  };

  const timeWorked = activeEntry && isTimeSynced ? calculateTimeWorked(activeEntry.clock_in_time) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white p-6">
      <div className="container mx-auto max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <Button 
            onClick={onBack} 
            variant="outline"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Store Station
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">Employee Timing</h1>
          <div></div>
        </div>

        {/* Server Time Display */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-blue-600" />
                <span className="text-lg font-semibold text-gray-900">Current Time</span>
              </div>
              <div className="text-3xl font-mono text-blue-600">
                {serverTime.toLocaleTimeString()}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                {serverTime.toLocaleDateString()}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Live Timer for Active Sessions */}
        {activeEntry && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Timer className="w-5 h-5 text-green-500" />
                  <span className="text-lg font-semibold">Time Worked Today</span>
                </div>
                {timeWorked && (
                  <div className="text-4xl font-mono font-bold text-green-600">
                    {formatDuration(timeWorked.hours, timeWorked.minutes, timeWorked.seconds)}
                  </div>
                )}
                <div className="text-sm text-gray-500 mt-2">
                  Started at: {new Date(activeEntry.clock_in_time).toLocaleTimeString()}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!employee ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Enter Employee ID
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="employeeId">Employee ID</Label>
                <Input
                  id="employeeId"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  placeholder="Enter your employee ID"
                  onKeyPress={(e) => e.key === 'Enter' && lookupEmployee()}
                  className="text-lg p-6"
                />
              </div>
              <Button 
                onClick={lookupEmployee} 
                disabled={isLoading || !employeeId.trim()}
                className="w-full text-lg p-6"
              >
                {isLoading ? "Looking up..." : "Continue"}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Welcome, {employee.first_name} {employee.last_name}</CardTitle>
              <p className="text-sm text-gray-600">Employee ID: {employee.employee_id}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {activeEntry ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-700">
                      <Clock className="w-5 h-5" />
                      <span className="font-medium">Currently Clocked In</span>
                    </div>
                    <p className="text-sm text-green-600 mt-1">
                      Since: {new Date(activeEntry.clock_in_time).toLocaleString()}
                    </p>
                  </div>
                  <Button 
                    onClick={clockOut}
                    disabled={isLoading}
                    variant="destructive"
                    className="w-full text-lg p-6"
                  >
                    {isLoading ? "Clocking Out..." : "Clock Out"}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-blue-700">
                      <Clock className="w-5 h-5" />
                      <span className="font-medium">Ready to Clock In</span>
                    </div>
                    <p className="text-sm text-blue-600 mt-1">
                      Tap the button below to start your shift
                    </p>
                  </div>
                  <Button 
                    onClick={clockIn}
                    disabled={isLoading}
                    className="w-full text-lg p-6"
                  >
                    {isLoading ? "Clocking In..." : "Clock In"}
                  </Button>
                </div>
              )}
              
              <Button 
                onClick={reset}
                variant="outline"
                className="w-full"
              >
                Switch Employee
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
